'use client';
import { useEffect, useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

type ServiceCategory = 'all' | 'strategy-audits' | 'growth-sprints' | 'implementation' | 'ongoing-execution' | 'systems-automation' | 'diy-resources';

interface Service {
  id: string;
  category: ServiceCategory;
  icon: string;
  title: string;
  description: string;
  deliverables: string[];
  timeRange: string;
  slug: string;
}

export default function ServicesPage() {
  const [activeCategory, setActiveCategory] = useState<ServiceCategory>('all');

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [activeCategory]);

  const services: Service[] = [
    // Strategy & Bottleneck Audits
    {
      id: 'svc-strategy',
      category: 'strategy-audits',
      icon: 'MagnifyingGlassCircleIcon',
      title: 'Growth Bottleneck Audit',
      description: 'Identify exactly where revenue is leaking and what to fix first — a structured diagnostic that maps your growth constraints and delivers a prioritized execution roadmap.',
      deliverables: [
        'Bottleneck identification & gap analysis',
        'Channel strategy & budget allocation',
        '90-day prioritized execution roadmap'
      ],
      timeRange: '2–4 weeks',
      slug: 'strategy'
    },
    {
      id: 'svc-ops-strategy',
      category: 'strategy-audits',
      icon: 'ChartPieIcon',
      title: 'Operational Strategy Consultation',
      description: 'Get an outside perspective on how to run your business more profitably — bottlenecks identified, inefficiencies mapped, and a clear roadmap delivered.',
      deliverables: [
        'Operational assessment & gap analysis',
        'Efficiency & cost improvement recommendations',
        '90-day operational roadmap'
      ],
      timeRange: '2–4 weeks',
      slug: 'operational-strategy-consultation'
    },
    {
      id: 'svc-market-research',
      category: 'strategy-audits',
      icon: 'LightBulbIcon',
      title: 'Deep/Wide Market Research',
      description: 'Make decisions backed by real market intelligence — comprehensive research delivered as actionable strategy with competitive landscape and customer segment analysis.',
      deliverables: [
        'Market sizing & competitive landscape',
        'Customer segment & trend analysis',
        'Executive report with recommendations'
      ],
      timeRange: '2–4 weeks',
      slug: 'market-research'
    },
    {
      id: 'svc-pricing',
      category: 'strategy-audits',
      icon: 'CurrencyDollarIcon',
      title: 'Pricing Analysis & Suggestion',
      description: 'Stop leaving money on the table — data-driven pricing benchmarks and strategy to maximize revenue and position your offer correctly in the market.',
      deliverables: [
        'Competitive pricing benchmarks',
        'Value-based pricing recommendations',
        'Revenue impact projections & roadmap'
      ],
      timeRange: '2–3 weeks',
      slug: 'pricing-analysis'
    },

    // Growth Sprints
    {
      id: 'svc-paid-search',
      category: 'growth-sprints',
      icon: 'MagnifyingGlassIcon',
      title: 'Paid Search',
      description: 'Drive high-intent traffic with data-driven Google and Bing ad campaigns that convert. Capture buyers actively searching for your solution.',
      deliverables: [
        'Keyword research & campaign structure',
        'Ad copywriting & A/B testing',
        'Bid management & monthly reporting'
      ],
      timeRange: '4–8 weeks',
      slug: 'paid-search'
    },
    {
      id: 'svc-paid-social',
      category: 'growth-sprints',
      icon: 'MegaphoneIcon',
      title: 'Paid Social',
      description: 'Reach your ideal audience on Meta, TikTok, and LinkedIn with creative-led social campaigns that drive awareness, leads, and conversions.',
      deliverables: [
        'Audience research & targeting setup',
        'Ad creative production & testing',
        'Campaign optimization & reporting'
      ],
      timeRange: '4–8 weeks',
      slug: 'paid-social'
    },
    {
      id: 'svc-paid-media',
      category: 'growth-sprints',
      icon: 'ChartBarIcon',
      title: 'Paid Media Management',
      description: 'Scale profitably with data-driven ad campaigns across all paid channels. Multi-platform execution with consistent ROAS tracking and optimization.',
      deliverables: [
        'Multi-channel campaign setup & optimization',
        'Creative testing & performance analysis',
        'Monthly reporting with actionable insights'
      ],
      timeRange: '4–12 weeks',
      slug: 'paid-media-management'
    },
    {
      id: 'svc-seo',
      category: 'growth-sprints',
      icon: 'RocketLaunchIcon',
      title: 'SEO',
      description: 'Grow organic traffic and rankings with technical SEO, content strategy, and authority building that compounds over time.',
      deliverables: [
        'Technical audit & on-page optimization',
        'Keyword strategy & content roadmap',
        'Link building & monthly ranking reports'
      ],
      timeRange: '8–16 weeks',
      slug: 'seo'
    },
    {
      id: 'svc-geo-aeo',
      category: 'growth-sprints',
      icon: 'MapIcon',
      title: 'GEO | AEO',
      description: 'Optimize your brand for AI-powered search engines and answer engines to capture next-gen visibility in ChatGPT, Perplexity, and Google SGE.',
      deliverables: [
        'AI search visibility audit',
        'Entity optimization & structured data',
        'Answer-ready content strategy'
      ],
      timeRange: '4–8 weeks',
      slug: 'geo-aeo'
    },

    // Implementation & Buildouts
    {
      id: 'svc-web-design',
      category: 'implementation',
      icon: 'GlobeAltIcon',
      title: 'Website & Funnel Build',
      description: 'Convert more visitors with high-performance websites and sales funnels built to grow your business — from landing pages to full site builds.',
      deliverables: [
        'Custom website or landing page design',
        'Conversion-optimized funnel build',
        'Technical setup & integrations'
      ],
      timeRange: '4–8 weeks',
      slug: 'web-design'
    },
    {
      id: 'svc-branding',
      category: 'implementation',
      icon: 'PaintBrushIcon',
      title: 'Brand & Creative',
      description: 'Stand out with brand identity and creative that converts — from logo to full visual system, built to support every channel you run.',
      deliverables: [
        'Brand strategy & positioning',
        'Visual identity system',
        'Creative campaign development'
      ],
      timeRange: '4–6 weeks',
      slug: 'branding-design'
    },
    {
      id: 'svc-content',
      category: 'implementation',
      icon: 'DocumentTextIcon',
      title: 'Content Production',
      description: 'Turn your brand into a content machine with high-converting articles, copy, and creative assets built around your audience and growth goals.',
      deliverables: [
        'Content strategy & editorial calendar',
        'Blog posts, landing pages & ad copy',
        'Performance tracking & optimization'
      ],
      timeRange: '2–4 weeks',
      slug: 'content'
    },
    {
      id: 'svc-photo-video',
      category: 'implementation',
      icon: 'FilmIcon',
      title: 'Photo & Video',
      description: 'Professional photo and video production that elevates your brand and drives engagement — from concept through final delivery.',
      deliverables: [
        'Pre-production planning & shot list',
        'On-site production with full crew',
        'Post-production editing & delivery'
      ],
      timeRange: '2–4 weeks',
      slug: 'photo-video'
    },
    {
      id: 'svc-pitch-deck',
      category: 'implementation',
      icon: 'PresentationChartBarIcon',
      title: 'Pitch Deck & Presentation',
      description: 'Walk into every room with a deck that closes deals — investor-ready design and strategic storytelling that moves the conversation forward.',
      deliverables: [
        'Narrative structure & slide outline',
        'Copywriting & custom visual design',
        'Editable files in PowerPoint & PDF'
      ],
      timeRange: '2–4 weeks',
      slug: 'pitch-deck-presentation'
    },

    // Ongoing Execution & Optimization
    {
      id: 'svc-growth',
      category: 'ongoing-execution',
      icon: 'ArrowTrendingUpIcon',
      title: 'Growth Marketing',
      description: 'Build a predictable growth engine with full-funnel marketing execution and continuous optimization — shipped every week, not every quarter.',
      deliverables: [
        'Multi-channel strategy & execution',
        'Conversion rate optimization',
        'Email & SMS automation'
      ],
      timeRange: '8–16 weeks',
      slug: 'growth-marketing'
    },
    {
      id: 'svc-lifecycle',
      category: 'ongoing-execution',
      icon: 'ArrowPathIcon',
      title: 'Lifecycle Marketing',
      description: 'Nurture customers from first touch to loyal advocate with automated email and SMS journeys that maximize lifetime value.',
      deliverables: [
        'Customer journey mapping',
        'Email & SMS automation flows',
        'Segmentation & personalization'
      ],
      timeRange: '4–8 weeks',
      slug: 'lifecycle-marketing'
    },
    {
      id: 'svc-social-media',
      category: 'ongoing-execution',
      icon: 'ChatBubbleLeftRightIcon',
      title: 'Social Media Management',
      description: 'Turn your brand into a lead-generating machine with full-service social media management — content, community, and conversion built in.',
      deliverables: [
        'Content strategy & scheduling',
        'Caption writing & community management',
        'Lead magnet creation & funnel integration'
      ],
      timeRange: '4–8 weeks',
      slug: 'social-media'
    },
    {
      id: 'svc-affiliate',
      category: 'ongoing-execution',
      icon: 'LinkIcon',
      title: 'Performance Marketing',
      description: 'Build and scale a high-performing partner program that drives revenue on a pay-for-performance model with ongoing relationship management.',
      deliverables: [
        'Program setup & commission structure',
        'Partner recruitment & onboarding',
        'Relationship management & growth'
      ],
      timeRange: '4–8 weeks',
      slug: 'affiliate-marketing'
    },
    {
      id: 'svc-reputation',
      category: 'ongoing-execution',
      icon: 'StarIcon',
      title: 'Reputation Management',
      description: 'Protect and grow your online reputation with proactive review management and brand monitoring that builds trust at scale.',
      deliverables: [
        'Review platform setup & monitoring',
        'Response strategy & templates',
        'Reputation reporting & insights'
      ],
      timeRange: '4–8 weeks',
      slug: 'reputation-management'
    },

    // Systems, Automation & Internal Tools
    {
      id: 'svc-ops-structure',
      category: 'systems-automation',
      icon: 'BuildingOfficeIcon',
      title: 'Internal Operational Structure',
      description: 'Build a business that runs without you in every decision — org design, roles, reporting lines, and decision-making frameworks defined.',
      deliverables: [
        'Org chart & role definition documents',
        'Decision-making framework',
        'Hiring roadmap & rollout plan'
      ],
      timeRange: '2–4 weeks',
      slug: 'internal-operational-structure'
    },
    {
      id: 'svc-sop',
      category: 'systems-automation',
      icon: 'ClipboardDocumentListIcon',
      title: 'Internal SOP Assistance',
      description: 'Document how your business works so it can run without you explaining it every time — step-by-step SOPs that enable delegation and scale.',
      deliverables: [
        'Process discovery & mapping',
        'SOP writing with flowcharts',
        'SOP library & training guide'
      ],
      timeRange: '2–4 weeks',
      slug: 'internal-sop-assistance'
    },
    {
      id: 'svc-pr',
      category: 'systems-automation',
      icon: 'SpeakerWaveIcon',
      title: 'Public Relations',
      description: 'Build brand authority and media presence with strategic PR campaigns and earned media placements that support your growth narrative.',
      deliverables: [
        'PR strategy & media list building',
        'Press release writing & outreach',
        'Media coverage tracking & reporting'
      ],
      timeRange: '4–8 weeks',
      slug: 'public-relations'
    },
    {
      id: 'svc-amazon',
      category: 'systems-automation',
      icon: 'ShoppingBagIcon',
      title: 'Amazon Services',
      description: 'Grow your Amazon presence with listing optimization, PPC management, and brand store builds that drive visibility and conversions.',
      deliverables: [
        'Listing optimization & A+ content',
        'Amazon PPC setup & management',
        'Brand store design & reporting'
      ],
      timeRange: '4–8 weeks',
      slug: 'amazon-services'
    },

    // DIY Resources
    {
      id: 'svc-influencer',
      category: 'diy-resources',
      icon: 'UserGroupIcon',
      title: 'Influencer Sourcing',
      description: 'Find the right influencers who actually move the needle — vetted lists across YouTube, Instagram, and TikTok with audience quality analysis.',
      deliverables: [
        'Platform research & influencer vetting',
        'Audience quality & engagement analysis',
        'Shortlist with outreach templates'
      ],
      timeRange: '2–4 weeks',
      slug: 'influencer-sourcing'
    },
    {
      id: 'svc-ooh',
      category: 'diy-resources',
      icon: 'MapPinIcon',
      title: 'Out of Home Marketing',
      description: 'Make an impact in the physical world with strategic billboard, transit, and OOH advertising for local and regional campaigns.',
      deliverables: [
        'Market & location research',
        'Creative production & placement',
        'Campaign measurement & reporting'
      ],
      timeRange: '4–8 weeks',
      slug: 'out-of-home-marketing'
    },
    {
      id: 'svc-podcast',
      category: 'diy-resources',
      icon: 'MicrophoneIcon',
      title: 'Podcast Generation',
      description: 'Launch and grow a podcast that builds your audience and authority — end-to-end production and distribution for founders and operators.',
      deliverables: [
        'Concept, branding & platform setup',
        'Recording, editing & show notes',
        'Distribution & social clips per episode'
      ],
      timeRange: '2–4 weeks',
      slug: 'podcast-generation'
    },
    {
      id: 'svc-media-buying',
      category: 'diy-resources',
      icon: 'CurrencyDollarIcon',
      title: 'Media Buying',
      description: 'Maximize reach and ROI with strategic media planning and buying across digital and traditional channels for larger campaign budgets.',
      deliverables: [
        'Media plan & channel strategy',
        'Rate negotiation & placement booking',
        'Campaign trafficking & optimization'
      ],
      timeRange: '4–8 weeks',
      slug: 'media-buying'
    },
  ];

  const categories = [
    { id: 'all', label: 'All Services' },
    { id: 'strategy-audits', label: 'Strategy & Audits' },
    { id: 'growth-sprints', label: 'Growth Sprints' },
    { id: 'implementation', label: 'Implementation' },
    { id: 'ongoing-execution', label: 'Ongoing Execution' },
    { id: 'systems-automation', label: 'Systems & Automation' },
    { id: 'diy-resources', label: 'DIY & Specialized' },
  ];

  const filteredServices = activeCategory === 'all'
    ? services
    : services.filter(s => s.category === activeCategory);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal">
              <SectionHeader
                eyebrow="How We Work"
                title="Diagnose. Sprint. Build. Execute. Scale."
                description="We don't offer everything. We follow a deliberate sequence — identify the bottleneck, run a focused sprint, build the infrastructure, then execute and optimize every week."
                align="center"
                className="mb-16"
              />
            </div>

            {/* Filter Chips */}
            <div className="reveal flex flex-wrap justify-center gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id as ServiceCategory)}
                  className={`px-6 py-2 text-micro uppercase tracking-widest font-medium border transition-all ${
                    activeCategory === cat.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-transparent text-muted-foreground border-border hover:border-primary hover:text-primary'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 px-6">
          <div className="max-w-[1600px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service, index) => (
                <div
                  key={service.id}
                  className={`reveal stagger-${(index % 3) + 1} group p-8 border border-border hover:border-primary transition-all duration-500 bg-card flex flex-col`}
                >
                  {/* Icon & Category Badge */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 border border-border group-hover:border-primary transition-colors flex items-center justify-center">
                      <Icon name={service.icon as any} size={24} className="text-primary" />
                    </div>
                    <Badge variant="default" className="text-[9px]">
                      {categories.find(c => c.id === service.category)?.label}
                    </Badge>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-semibold tracking-tight mb-4 text-card-foreground">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Deliverables */}
                  <div className="mb-6 flex-grow">
                    <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium block mb-3">
                      Deliverables
                    </span>
                    <ul className="space-y-2">
                      {service.deliverables.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-body-sm">
                          <Icon name="CheckCircleIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Time Range */}
                  <div className="mb-6 p-4 bg-surface-1 border border-border">
                    <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium block mb-2">
                      Typical Timeline
                    </span>
                    <p className="text-body-sm text-foreground">{service.timeRange}</p>
                  </div>

                  {/* CTA */}
                  <Link href={`/services/${service.slug}`} className="w-full">
                    <Button variant="primary" size="md" className="w-full">
                      Learn More
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Work Directly With Daniel — Premium Advisory Section */}
        <section className="py-24 px-6 border-t border-border bg-[#0A0A0A]">
          <div className="max-w-[1600px] mx-auto">
            {/* Section Header */}
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">
                Founder-Led Advisory
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                Work Directly With Daniel
              </h2>
              <p className="text-lg text-gray-400 mb-6 max-w-2xl mx-auto">
                For founders and operators who want direct access to strategy, AI systems, and execution leverage — not a junior account manager.
              </p>
              <p className="text-body text-gray-500 max-w-3xl mx-auto leading-relaxed">
                Most businesses don't need more generic advice. They need sharper thinking, cleaner workflows, and a faster path to execution. Work With Daniel is a founder-led advisory offer for operators who are ready to move.
              </p>
            </div>

            {/* Premium Card */}
            <div className="reveal max-w-3xl mx-auto">
              <div className="relative p-12 bg-[#0D0D0D] border border-[#DC2626]/40 shadow-[0_0_60px_rgba(220,38,38,0.08)]">
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#DC2626] to-transparent" />

                {/* Eyebrow */}
                <span className="text-micro uppercase tracking-ultra-wide text-[#DC2626] block mb-6">
                  Private Advisory · Limited Availability
                </span>

                {/* Title */}
                <h3 className="text-3xl lg:text-4xl font-bold tracking-tight mb-6 text-white">
                  Work With Daniel
                </h3>

                {/* Body */}
                <p className="text-body text-gray-400 leading-relaxed mb-10">
                  Private, founder-led advisory for founders and operators who want high-level help with AI, systems, automation, marketing strategy, workflow design, and execution leverage.
                </p>

                {/* Bullets */}
                <ul className="space-y-4 mb-12">
                  {[
                    'AI advisory and workflow design',
                    'Context, prompting, and systems thinking',
                    'Founder-level strategy and decision support',
                    'Direct access, limited availability',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-4">
                      <span className="mt-1.5 w-1.5 h-1.5 bg-[#DC2626] flex-shrink-0" />
                      <span className="text-body-sm text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <Link href="/work-with-daniel">
                    <button className="px-8 py-4 bg-[#DC2626] text-white text-[10px] uppercase tracking-widest font-semibold hover:bg-[#b91c1c] transition-colors">
                      Explore Work With Daniel
                    </button>
                  </Link>
                  <p className="text-micro uppercase tracking-ultra-wide text-gray-600">
                    Limited founder capacity. Best fit for businesses ready to implement.
                  </p>
                </div>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#DC2626]/40 to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 border-t border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal text-center">
              <h2 className="text-4xl font-bold mb-6">Start with a Sprint. Not a retainer.</h2>
              <p className="text-body text-muted-foreground mb-8 max-w-2xl mx-auto">
                Every engagement begins with a paid diagnostic sprint — we identify your bottlenecks, map the highest-value opportunities, and deliver a prioritized roadmap. Then you choose your path: take the roadmap in-house, engage us for a fixed buildout, or move into ongoing execution.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book">
                  <Button variant="primary" size="lg">
                    Book a Sprint
                  </Button>
                </Link>
                <Link href="/pricing">
                  <Button variant="outline" size="lg">
                    See What a Sprint Uncovers
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Not Ready Yet CTA — DIY Trust Builder */}
        <section className="py-16 px-6 border-t border-border">
          <div className="max-w-[900px] mx-auto">
            <div className="reveal flex flex-col md:flex-row items-center justify-between gap-8 p-8 border border-border bg-card">
              <div className="flex-1">
                <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium block mb-2">Not ready to engage yet?</span>
                <h3 className="text-2xl font-bold mb-2">Start with our free toolkit.</h3>
                <p className="text-body-sm text-muted-foreground">
                  14 free templates, guides, and frameworks — the same tools we use with clients, packaged for self-service. No credit card. No catch.
                </p>
              </div>
              <div className="shrink-0">
                <Link href="/diy">
                  <Button variant="secondary" size="lg">
                    Browse Free Templates
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}